/**
 * StrategyEditor — Elite users configure custom investment strategies.
 *
 * 6 sliders (cash_flow, cash_on_cash, cap_rate, appreciation, rent_to_price,
 * risk) that must sum to 100. Presets are one-click templates; picking one
 * fills the sliders and lets the user tweak from there.
 *
 * Used inside a Dialog from the Strategy Picker on the search results.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sliders, RotateCcw, Save, X, Trash2, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "../lib/helpers";

const FACTORS = [
  { key: "cash_flow", label: "Cash Flow", hint: "Monthly cash left after every expense" },
  { key: "cash_on_cash", label: "Cash-on-Cash Return", hint: "Annual cash flow ÷ money you put in" },
  { key: "cap_rate", label: "Cap Rate", hint: "NOI ÷ purchase price — pure yield" },
  { key: "appreciation", label: "Appreciation Potential", hint: "Zestimate gap vs purchase price" },
  { key: "rent_to_price", label: "Rent-to-Price Ratio", hint: "The 1% rule as a spectrum" },
  { key: "risk", label: "Risk (Low = better)", hint: "Inverse of expense ratio + vacancy" },
];

const EMPTY_WEIGHTS = { cash_flow: 25, cash_on_cash: 25, cap_rate: 20, appreciation: 15, rent_to_price: 10, risk: 5 };

const PROPERTY_TYPE_OPTIONS = [
  { key: "single_family", label: "Single Family" },
  { key: "multi_family", label: "Multi-Family" },
  { key: "townhouse", label: "Townhouse" },
  { key: "condo", label: "Condo" },
  { key: "apartment", label: "Apartment" },
  { key: "manufactured", label: "Manufactured" },
];

const RENTAL_STRATEGY_OPTIONS = [
  { key: "", label: "Any" },
  { key: "long_term", label: "Long-Term Rental" },
  { key: "short_term", label: "Short-Term / Airbnb" },
  { key: "brrrr", label: "BRRRR" },
  { key: "flip", label: "Fix & Flip" },
  { key: "house_hack", label: "House Hack" },
];

const RISK_OPTIONS = [
  { key: "", label: "Any" },
  { key: "low", label: "Low — safe & stable" },
  { key: "medium", label: "Medium" },
  { key: "high", label: "High — swing big" },
];

export default function StrategyEditor({ initial, onSave, onCancel, onDelete }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [weights, setWeights] = useState(initial?.weights || EMPTY_WEIGHTS);
  const [criteria, setCriteria] = useState(initial?.criteria || {});
  const [locationsText, setLocationsText] = useState((initial?.criteria?.locations || []).join(", "));
  const [presets, setPresets] = useState([]);
  const [saving, setSaving] = useState(false);

  const setCrit = (key, value) => setCriteria((c) => ({ ...c, [key]: value }));
  const togglePropType = (key) => setCriteria((c) => {
    const list = c.property_types || [];
    return { ...c, property_types: list.includes(key) ? list.filter((t) => t !== key) : [...list, key] };
  });

  const buildCriteria = () => {
    const out = {};
    const num = (k) => {
      const v = criteria[k];
      if (v !== "" && v != null && !Number.isNaN(Number(v))) out[k] = Number(v);
    };
    num("budget_max"); num("budget_min"); num("min_cash_flow"); num("min_coc"); num("min_cap_rate"); num("down_payment_pct");
    if ((criteria.property_types || []).length) out.property_types = criteria.property_types;
    const locs = locationsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (locs.length) out.locations = locs.slice(0, 10);
    if (criteria.rental_strategy) out.rental_strategy = criteria.rental_strategy;
    if (criteria.risk_tolerance) out.risk_tolerance = criteria.risk_tolerance;
    return out;
  };

  useEffect(() => {
    axios.get(`${API}/strategies/presets`)
      .then(r => setPresets(r.data.presets || []))
      .catch(() => {});
  }, []);

  const total = Object.values(weights).reduce((a, b) => a + Number(b || 0), 0);
  const isValid = Math.abs(total - 100) <= 5 && name.trim().length > 0;

  const applyPreset = (preset) => {
    setWeights({ ...preset.weights });
    if (!name) setName(preset.name);
    toast.info(`Loaded "${preset.name}" — tweak it and save your own`);
  };

  const handleSlider = (key, value) => {
    setWeights(w => ({ ...w, [key]: Number(value) }));
  };

  const normalize = () => {
    if (total === 0) { setWeights(EMPTY_WEIGHTS); return; }
    const scale = 100 / total;
    const scaled = Object.fromEntries(
      Object.entries(weights).map(([k, v]) => [k, Math.round(v * scale)])
    );
    // Fix any rounding drift by nudging the largest bucket.
    const drift = 100 - Object.values(scaled).reduce((a, b) => a + b, 0);
    if (drift !== 0) {
      const biggest = Object.entries(scaled).sort((a, b) => b[1] - a[1])[0][0];
      scaled[biggest] += drift;
    }
    setWeights(scaled);
  };

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim(), weights, criteria: buildCriteria() };
      let saved;
      if (initial?.strategy_id) {
        const r = await axios.put(`${API}/strategies/${initial.strategy_id}`, body);
        saved = { ...initial, ...body, ...r.data };
      } else {
        const r = await axios.post(`${API}/strategies`, body);
        saved = r.data;
      }
      toast.success(initial?.strategy_id ? "Strategy updated" : "Strategy saved");
      onSave?.(saved);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("Custom Deal Scoring is an Elite feature — upgrade to save strategies");
      } else {
        toast.error(err.response?.data?.detail || "Failed to save strategy");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!initial?.strategy_id) return;
    if (!window.confirm(`Delete strategy "${initial.name}"?`)) return;
    try {
      await axios.delete(`${API}/strategies/${initial.strategy_id}`);
      toast.success("Strategy deleted");
      onDelete?.(initial.strategy_id);
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="strategy-editor">
      {/* Preset row */}
      <div>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Start from a preset
        </p>
        <div className="flex flex-wrap gap-1.5">
          {presets.map(p => (
            <Button
              key={p.id}
              variant="outline"
              size="sm"
              className="h-7 text-[11px] gap-1"
              onClick={() => applyPreset(p)}
              data-testid={`strategy-preset-${p.id}`}
            >
              <Sparkles className="w-3 h-3 text-amber-500" />
              {p.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Name + description */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-wide">
            Strategy Name
          </label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Tampa Cash Flow"
            className="mt-1 h-8 text-sm"
            maxLength={60}
            data-testid="strategy-name-input"
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-wide">
            Description (optional)
          </label>
          <Input
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What's this strategy optimized for?"
            className="mt-1 h-8 text-sm"
            maxLength={200}
            data-testid="strategy-description-input"
          />
        </div>
      </div>

      {/* Sliders */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Weight each factor · must sum to 100
          </p>
          <div className={`text-xs font-mono font-bold ${total === 100 ? "text-emerald-500" : Math.abs(total - 100) <= 5 ? "text-amber-500" : "text-red-500"}`}>
            {total}/100
          </div>
        </div>
        <div className="space-y-2">
          {FACTORS.map(f => (
            <div key={f.key} className="flex items-center gap-3" data-testid={`strategy-factor-${f.key}`}>
              <div className="w-40 sm:w-48 flex-shrink-0">
                <p className="text-xs text-foreground">{f.label}</p>
                <p className="text-[10px] text-muted-foreground">{f.hint}</p>
              </div>
              <input
                type="range"
                min="0"
                max="60"
                value={weights[f.key] || 0}
                onChange={e => handleSlider(f.key, e.target.value)}
                className="flex-1 accent-amber-500"
                data-testid={`strategy-slider-${f.key}`}
              />
              <div className="w-12 flex-shrink-0 text-right font-mono text-xs font-bold">
                {weights[f.key] || 0}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Investor criteria — drives the Strategy Match score */}
      <div className="border-t border-border pt-3" data-testid="strategy-criteria-section">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
          Investor Criteria <span className="normal-case font-normal">(optional — powers your "Strategy Match" score)</span>
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <div>
            <label className="text-[10px] text-muted-foreground">Max Budget ($)</label>
            <Input type="number" min="0" value={criteria.budget_max ?? ""} onChange={(e) => setCrit("budget_max", e.target.value)} placeholder="e.g. 350000" className="mt-0.5 h-8 text-xs" data-testid="criteria-budget-max" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Min Cash Flow ($/mo)</label>
            <Input type="number" min="0" value={criteria.min_cash_flow ?? ""} onChange={(e) => setCrit("min_cash_flow", e.target.value)} placeholder="e.g. 200" className="mt-0.5 h-8 text-xs" data-testid="criteria-min-cash-flow" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Min CoC (%)</label>
            <Input type="number" min="0" max="100" step="0.5" value={criteria.min_coc ?? ""} onChange={(e) => setCrit("min_coc", e.target.value)} placeholder="e.g. 8" className="mt-0.5 h-8 text-xs" data-testid="criteria-min-coc" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Min Cap Rate (%)</label>
            <Input type="number" min="0" max="30" step="0.5" value={criteria.min_cap_rate ?? ""} onChange={(e) => setCrit("min_cap_rate", e.target.value)} placeholder="e.g. 6" className="mt-0.5 h-8 text-xs" data-testid="criteria-min-cap-rate" />
          </div>
        </div>
        <div className="mb-2">
          <label className="text-[10px] text-muted-foreground">Property Types</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {PROPERTY_TYPE_OPTIONS.map((t) => {
              const active = (criteria.property_types || []).includes(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => togglePropType(t.key)}
                  className={`px-2 py-1 rounded-full text-[10px] font-medium border transition-colors ${active ? "bg-amber-500/15 text-amber-500 border-amber-500/40" : "bg-secondary/40 text-muted-foreground border-border hover:text-foreground"}`}
                  data-testid={`criteria-ptype-${t.key}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-muted-foreground">Target Locations (comma-separated)</label>
            <Input value={locationsText} onChange={(e) => setLocationsText(e.target.value)} placeholder="Tampa FL, 33634, Orlando FL" className="mt-0.5 h-8 text-xs" data-testid="criteria-locations" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Rental Strategy</label>
            <select
              value={criteria.rental_strategy || ""}
              onChange={(e) => setCrit("rental_strategy", e.target.value)}
              className="mt-0.5 h-8 text-xs w-full rounded-md border border-border bg-background px-2"
              data-testid="criteria-rental-strategy"
            >
              {RENTAL_STRATEGY_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Risk Tolerance</label>
            <select
              value={criteria.risk_tolerance || ""}
              onChange={(e) => setCrit("risk_tolerance", e.target.value)}
              className="mt-0.5 h-8 text-xs w-full rounded-md border border-border bg-background px-2"
              data-testid="criteria-risk-tolerance"
            >
              {RISK_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 text-muted-foreground"
            onClick={normalize}
            data-testid="strategy-normalize-btn"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Auto-normalize to 100
          </Button>
          {initial?.strategy_id && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 text-red-500 hover:text-red-600"
              onClick={handleDelete}
              data-testid="strategy-delete-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs h-7" data-testid="strategy-cancel-btn">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isValid || saving}
            className="text-xs h-7 bg-amber-500 hover:bg-amber-600 text-black font-bold"
            data-testid="strategy-save-btn"
          >
            <Save className="w-3 h-3 mr-1" />
            {saving ? "Saving…" : (initial?.strategy_id ? "Update Strategy" : "Save Strategy")}
          </Button>
        </div>
      </div>
    </div>
  );
}
